import produce from 'immer';
import reject from 'lodash/reject';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { bffActivePendingItemsUrl } from 'src/app/client/api';
import { fetchActivePending, fetchPendingTransferItems } from 'src/app/client/models/account';
import { cartRemoveItems, segmentTrack } from 'src/app/state/actions';
import {
  CLOSE_NEXT_BEST_ACTION_CARD,
  FETCH_MEDS_FAILURE,
  FETCH_MEDS_REQUEST,
  FETCH_MEDS_SUCCESS,
  FETCH_PENDING_TRANSFERS_SUCCESS,
  TOGGLE_HOME_SCREEN_ARCHIVE_SECTION,
} from 'src/app/state/actions/types';
import {
  getCartItems,
  getCurrentAccountId,
  getHasPrimaryPatientEverTransferred,
  getMedicationManagementData,
  getPendingTransfers,
  getUnarchivedMedications,
} from 'src/app/state/selectors';
import { getArchivedMedications } from 'src/app/state/slices/archivedMedications';
import type { Dispatch, RootState } from 'src/app/state/store';
import type { Home, HomeArchivedItem, HomeItem, HomePrescriptionItem } from 'src/app/types';
import { HomeItemType, PrescriptionStatuses, PrescriptionStatusTypes } from 'src/app/types';
import { extractErrorCodes } from 'src/app/utils';
import { filterPendingTransfersFromHomeItems, flattenPrescriptionItems } from 'src/app/utils/medicationUtils';
import { CTATypes, HomeProductStatus } from 'src/consts';

export function archiveItem(patientMedicationId: number | string, isArchivedSection: boolean) {
  return async (_dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;
    // If isArchivedSection is true meaning they are clicking on already archived medication,
    // then we will unarchive, else we will archive.
    const response = await client.medicationSetArchived(patientMedicationId, !isArchivedSection);
    if (response.status === 200) {
      return response;
    }
    // TODO: would propagate the error down but still need designs for error state on archiving/unarchiving a medication.
    return null;
  };
}

export function toggleArchiveSection(isOpen: boolean) {
  return {
    type: TOGGLE_HOME_SCREEN_ARCHIVE_SECTION,
    isOpen,
  };
}

export function closeNextBestActionCard(key: string) {
  return {
    type: CLOSE_NEXT_BEST_ACTION_CARD,
    key,
  };
}

const getTransfers = (items: HomePrescriptionItem[] | HomeArchivedItem[]) =>
  items.filter(item => item?.type === HomeItemType.transfer);

export function loadHomescreenMedications(newAccountId?: string) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;

    dispatch({
      type: FETCH_MEDS_REQUEST,
    });

    const accountId = newAccountId ?? getCurrentAccountId(state);
    const archivedItems = getArchivedMedications(state);

    try {
      const response = await fetchActivePending(client, { accountId: accountId });

      let pendingTransfers: HomePrescriptionItem[] = [];
      if (response?.data?.pending) {
        pendingTransfers = response?.data?.pending ?? [];
      } else {
        pendingTransfers = filterPendingTransfersFromHomeItems(response?.data?.active);
      }

      const responseData: Home = response?.data;

      const primaryPatientActiveItems = responseData?.active?.find(item => !!item?.patient?.isPrimary)?.items ?? [];
      const primaryPatientArchivedItems = archivedItems?.filter(item => !!item?.isPrimary) ?? [];

      const primaryPatientActiveTransfers = getTransfers(primaryPatientActiveItems);
      const primaryPatientPendingTransfers = pendingTransfers.filter(item => !!item?.isPrimary) ?? [];
      const primaryPatientArchivedTransfers = getTransfers(primaryPatientArchivedItems);

      const hasActiveTransfer = primaryPatientActiveTransfers.length > 0;
      const hasPendingTransfer = primaryPatientPendingTransfers.length > 0;
      const hasArchivedTransfer = primaryPatientArchivedTransfers.length > 0;

      dispatch({
        type: FETCH_MEDS_SUCCESS,
        data: responseData,
        hasPrimaryEverSubmittedTransfer: hasActiveTransfer || hasPendingTransfer || hasArchivedTransfer,
        pendingTransfers: pendingTransfers,
      });
      const patientMedicationIdsWithRequestRefill = responseData?.active?.reduce(
        (result, patientMedicationGrouping) => {
          patientMedicationGrouping?.items?.forEach(medItem => {
            if (medItem.cta?.action?.type === CTATypes.requestRefill) {
              result.push(medItem.patientMedicationId);
            }
          });
          return result;
        },
        [] as number[]
      );
      const patientMedicationIdsWithProcessRefill = responseData?.active?.reduce(
        (result, patientMedicationGrouping) => {
          patientMedicationGrouping?.items?.forEach(medItem => {
            if (medItem.cta?.action?.type === CTATypes.processRefill) {
              result.push(medItem.patientMedicationId);
            }
          });
          return result;
        },
        [] as number[]
      );
      if (patientMedicationIdsWithRequestRefill?.length > 0) {
        dispatch(
          segmentTrack(UNIFIED.medicationCardHasCta, {
            patientmedication_ids: patientMedicationIdsWithRequestRefill,
            cta_type: 'request-refill',
            cta_variation: 'button',
          })
        );
      } else if (patientMedicationIdsWithProcessRefill?.length > 0) {
        dispatch(
          segmentTrack(UNIFIED.medicationCardHasCta, {
            patientmedication_ids: patientMedicationIdsWithProcessRefill,
            cta_type: 'process-refill',
            cta_variation: 'button',
          })
        );
      }
    } catch (err) {
      const bffError = err?.error ?? '';
      const api = bffActivePendingItemsUrl(accountId);
      const trackErrorPayload = {
        errorMessage: 'Loading Prescriptions Failed',
        errorSummary: `Title: ${bffError?.title ?? ''} | Flavor Text: ${bffError?.flavorText ?? ''}`,
        api,
        errorCode: extractErrorCodes(err),
        source: window.location.href,
      };

      segmentTrack(UNIFIED.error, trackErrorPayload);

      dispatch({
        type: FETCH_MEDS_FAILURE,
        error: err,
      });
    }
  };
}

export function updateMedicationCardToBeInRefillRequestedState(prescriptionId: string) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const medicationManagementData: { active: HomeItem[] } = getMedicationManagementData(state);
    const pendingTransfers = getPendingTransfers(state);
    const hasPrimaryEverSubmittedTransfer = getHasPrimaryPatientEverTransferred(state);
    const updatedMedicationManagementData = produce(medicationManagementData, draft => {
      for (
        let activeGroupingIndex = 0;
        activeGroupingIndex < medicationManagementData?.active?.length;
        activeGroupingIndex++
      ) {
        const activeGrouping = medicationManagementData?.active[activeGroupingIndex];
        for (let activeItemIndex = 0; activeItemIndex < activeGrouping?.items?.length; activeItemIndex++) {
          const activeItem = activeGrouping.items[activeItemIndex];
          if (activeItem?.id === prescriptionId) {
            draft.active[activeGroupingIndex].items[activeItemIndex].statusText =
              PrescriptionStatuses.refillRequestReceived;
            draft.active[activeGroupingIndex].items[activeItemIndex].refillStatus.status =
              PrescriptionStatuses.refillRequestReceived;
            draft.active[activeGroupingIndex].items[activeItemIndex].refillStatusText =
              PrescriptionStatusTypes.refillRequestReceived;
            draft.active[activeGroupingIndex].items[activeItemIndex].cta = null;
          }
        }
      }
    });

    dispatch({
      type: FETCH_MEDS_SUCCESS,
      data: { ...updatedMedicationManagementData },
      hasPrimaryEverSubmittedTransfer,
      pendingTransfers,
    });
  };
}

export function updateMedicationCardToBeInProcessRefillRequestedState(prescriptionId: string) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const medicationManagementData: { active: HomeItem[] } = getMedicationManagementData(state);
    const pendingTransfers = getPendingTransfers(state);
    const hasPrimaryEverSubmittedTransfer = getHasPrimaryPatientEverTransferred(state);
    const updatedMedicationManagementData = produce(medicationManagementData, draft => {
      for (
        let activeGroupingIndex = 0;
        activeGroupingIndex < medicationManagementData?.active?.length;
        activeGroupingIndex++
      ) {
        const activeGrouping = medicationManagementData?.active[activeGroupingIndex];
        for (let activeItemIndex = 0; activeItemIndex < activeGrouping?.items?.length; activeItemIndex++) {
          const activeItem = activeGrouping.items[activeItemIndex];
          if (activeItem?.id === prescriptionId) {
            draft.active[activeGroupingIndex].items[activeItemIndex].statusText =
              PrescriptionStatuses.processRefillRequestReceived;
            draft.active[activeGroupingIndex].items[activeItemIndex].refillStatus.status =
              PrescriptionStatuses.processRefillRequestReceived;
            draft.active[activeGroupingIndex].items[activeItemIndex].refillStatusText =
              PrescriptionStatusTypes.refillRequestReceived;
            draft.active[activeGroupingIndex].items[activeItemIndex].cta = null;
          }
        }
      }
    });

    dispatch({
      type: FETCH_MEDS_SUCCESS,
      data: { ...updatedMedicationManagementData },
      hasPrimaryEverSubmittedTransfer,
      pendingTransfers,
    });
  };
}

export function fetchHomescreen() {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();

    const cartItems = getCartItems(getState());
    const homescreenItems = getUnarchivedMedications(state);
    const readyToCheckoutFills =
      homescreenItems?.reduce((result, { items }) => {
        items.forEach(item => {
          if (item.status === HomeProductStatus.READY_TO_CHECKOUT && item.prescriptionFulfillmentId) {
            result[item.prescriptionFulfillmentId] = item;
          }
        });
        return result;
      }, {} as { [key: string]: HomePrescriptionItem }) ?? {};

    reject(cartItems, item => readyToCheckoutFills[item?.fulfillmentId ?? '']).forEach(item => {
      if (typeof item !== 'number' && 'fulfillmentId' in item) {
        dispatch(
          cartRemoveItems({
            fulfillmentId: item.fulfillmentId,
            productId: item.productId,
            price: item.price,
          })
        );
      }
    });
  };
}

export function fetchPendingTransfers(newAccountId?: string) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;

    dispatch({
      type: FETCH_MEDS_REQUEST,
    });

    try {
      const accountId = newAccountId ?? getCurrentAccountId(state);
      const response = await fetchPendingTransferItems(client, { accountId });
      dispatch({
        type: FETCH_PENDING_TRANSFERS_SUCCESS,
        pendingTransfers: flattenPrescriptionItems(response?.data),
      });
    } catch (err) {
      dispatch({
        type: FETCH_MEDS_FAILURE,
        error: err,
      });
    }
  };
}
